import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Shield,
  TrendingUp,
  Users,
  Target,
  Settings
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Target,
      title: "Business Automation Planning Session",
      description: "Strategic planning and roadmap development for implementing AI automation across your organization with expert guidance.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Settings,
      title: "AI Automation Solution Architecture Session",
      description: "Comprehensive design and architectural planning for your automation infrastructure and workflows.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Shield,
      title: "Vendor Evaluation & Scoring Session",
      description: "Expert evaluation and scoring of automation vendors to find the perfect fit for your business needs.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Users,
      title: "Vendor/Experts Coaching and Counseling",
      description: "Personalized coaching and counseling services to help vendors and experts optimize their automation solutions.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: TrendingUp,
      title: "Post-Automation QA & Review",
      description: "Quality assurance and performance review services to ensure your automation delivers optimal results.",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: Brain,
      title: "Team Automation Adaptation Training",
      description: "Comprehensive training programs to help your team adapt to and master new automation technologies and processes.",
      color: "from-teal-500 to-blue-500"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="features" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Our <span className="text-gradient">Consulting Services</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Expert guidance and strategic planning to help you implement AI automation successfully and achieve measurable business results.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ 
                y: -20, 
                scale: 1.05,
                rotateX: 5,
                rotateY: 5,
                transition: { duration: 0.4 }
              }}
              className="group perspective-1000"
            >
              <div className="bg-glass-effect rounded-2xl p-8 h-full border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 card-3d depth-shadow">
                <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-primary-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">100+</div>
            <div className="text-gray-400">Projects Completed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">50+</div>
            <div className="text-gray-400">Vendors Evaluated</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">95%</div>
            <div className="text-gray-400">Client Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">24/7</div>
            <div className="text-gray-400">Expert Support</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 